import { isUuid } from '@os/doc';
import { thumbHashToDataURL } from 'thumbhash';
import type { Api, RemoteAsset } from '../../lib/api';
import { mediaUrl } from '../../lib/media-url';
import type { MediaResolver, Variant } from '../editor/lod';
import type { LocalMediaStore } from './store';
import { resolveVariant, type StoredVariant } from './variants';

const BATCH_MAX = 200;

type Options = {
  boardId: string;
  api: Pick<Api, 'assetUrls'>;
  local: LocalMediaStore;
  base?: string;
  reducedMotion?: boolean;
  batchMs?: number;
  /** Delays before asking again for an asset the server does not know yet (upload or reference not there yet). */
  retryMs?: number[];
};

/**
 * Media of a server board (spec §10.2): this device's blobs first, otherwise R2 URLs from `asset_urls`, asked in
 * batches. An asset the server does not return yet (a peer still uploading, its reference not written) or returns
 * incomplete is asked again with backoff, so images appear on their own. Never throws.
 */
export class BoardMedia implements MediaResolver {
  private readonly remote = new Map<string, RemoteAsset>();
  private readonly queued = new Set<string>();
  private readonly waiting = new Set<string>(); // in flight or waiting for a retry
  private readonly attempts = new Map<string, number>();
  private readonly listeners = new Set<() => void>();
  private readonly placeholders = new Map<string, string | null>();
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private batch: ReturnType<typeof setTimeout> | null = null;
  private uploadingIds: ReadonlySet<string> = new Set();
  private destroyed = false;

  constructor(private readonly o: Options) {}

  urlFor(assetId: string, variant: Variant): string {
    const local = this.o.local.urlFor(assetId, variant);
    if (local) return local;
    const r = this.remote.get(assetId);
    if (r?.status !== 'ready') this.request(assetId); // no-op while a request or retry is pending
    const available = r ? (Object.keys(r.keys) as StoredVariant[]) : [];
    if (!r || available.length === 0) return '';
    const key = r.keys[resolveVariant({ mime: r.mime, expected: available }, variant, this.o.reducedMotion ?? false)];
    return key ? mediaUrl(key, this.o.base) : '';
  }

  /** Type and pixel size of an asset (this device's copy first), null while unknown. */
  info(assetId: string): { mime: string; w: number; h: number } | null {
    const local = this.o.local.get(assetId);
    if (local) return { mime: local.mime, w: local.w, h: local.h };
    const r = this.remote.get(assetId);
    return r ? { mime: r.mime, w: r.width, h: r.height } : null;
  }

  placeholder(assetId: string): string | null {
    const hash = this.o.local.get(assetId)?.thumbhash || this.remote.get(assetId)?.thumbhash;
    if (!hash) return null;
    if (!this.placeholders.has(hash)) {
      try {
        this.placeholders.set(hash, thumbHashToDataURL(Uint8Array.from(atob(hash), (c) => c.charCodeAt(0))));
      } catch {
        this.placeholders.set(hash, null);
      }
    }
    return this.placeholders.get(hash) ?? null;
  }

  uploading(assetId: string): boolean {
    return this.uploadingIds.has(assetId);
  }

  setUploading(ids: ReadonlySet<string>): void {
    const same = ids.size === this.uploadingIds.size && [...ids].every((id) => this.uploadingIds.has(id));
    this.uploadingIds = new Set(ids);
    if (!same) this.notify();
  }

  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  destroy(): void {
    this.destroyed = true;
    if (this.batch) clearTimeout(this.batch);
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.listeners.clear();
  }

  private request(assetId: string): void {
    if (this.destroyed || !isUuid(assetId) || this.waiting.has(assetId) || this.queued.has(assetId)) return;
    this.queued.add(assetId);
    this.batch ??= setTimeout(() => this.flush(), this.o.batchMs ?? 50);
  }

  private flush(): void {
    this.batch = null;
    const ids = [...this.queued];
    this.queued.clear();
    for (const id of ids) this.waiting.add(id);
    for (let i = 0; i < ids.length; i += BATCH_MAX) {
      const chunk = ids.slice(i, i + BATCH_MAX);
      this.o.api.assetUrls(this.o.boardId, chunk).then(
        (rows) => {
          if (this.destroyed) return;
          for (const r of rows) this.remote.set(r.assetId, r);
          for (const id of chunk) {
            if (this.remote.get(id)?.status === 'ready') {
              this.waiting.delete(id);
              this.attempts.delete(id);
            } else this.retry(id);
          }
          if (rows.length) this.notify();
        },
        () => chunk.forEach((id) => this.retry(id)),
      );
    }
  }

  private retry(assetId: string): void {
    if (this.destroyed) return;
    const delays = this.o.retryMs ?? [5_000, 15_000, 60_000];
    const n = this.attempts.get(assetId) ?? 0;
    this.attempts.set(assetId, n + 1);
    const t = setTimeout(() => {
      this.timers.delete(t);
      this.waiting.delete(assetId);
      this.request(assetId);
    }, delays[Math.min(n, delays.length - 1)]);
    this.timers.add(t);
  }

  private notify(): void {
    for (const l of [...this.listeners]) l();
  }
}
